import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { 
  Plus, Search, Edit2, Trash2, Smartphone, Download, FileText,
  Building2, MapPin, User, X, Save, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Copy, QrCode, Eye, Printer,
  UserPlus, UserMinus, Camera
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// @ts-ignore
import 'jspdf-autotable';
import Modal from './Modal';

interface HandheldTerminal {
  id: number;
  brand: string;
  model: string;
  inventory_number: string;
  serial_number: string;
  ip_address: string;
  mac_address: string;
  assigned_user_id?: number;
  assigned_user_name?: string;
  location_id: number;
  location_name?: string;
  department?: string;
  status?: 'Aktif' | 'Pasif' | 'Arızalı';
  fault_details?: string;
  created_at: string;
  updated_at?: string;
}

const HandheldTerminalsPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState<HandheldTerminal | null>(null);
  const [terminalsData, setTerminalsData] = useState<HandheldTerminal[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [ipDuplicateWarning, setIpDuplicateWarning] = useState<string>('');
  const [macDuplicateWarning, setMacDuplicateWarning] = useState<string>('');
  
  // Label printing state'leri
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedTerminals, setSelectedTerminals] = useState<number[]>([]);
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [labelSize, setLabelSize] = useState<string>('landscape-6'); // Default: 6 etiket/sayfa yatay

  // Single label printing state
  const [showSingleLabelModal, setShowSingleLabelModal] = useState(false);
  const [singleLabelTerminal, setSingleLabelTerminal] = useState<HandheldTerminal | null>(null);

  // History modal state'leri
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedTerminalHistory, setSelectedTerminalHistory] = useState<any[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTerminalForHistory, setSelectedTerminalForHistory] = useState<HandheldTerminal | null>(null);

  // Zimmet (atama / zimmet kaldırma) state'leri
  const HANDHELD_EQUIPMENT_LIST = [
    'Şarj Aleti', 'Şarj Kablosu', 'Şarj Standı', 'El Kayışı',
    'Koruyucu Kılıf', 'Ekran Koruyucu', 'Kullanım Kılavuzu', 'Kutu',
  ];
  const [assignTerminal, setAssignTerminal] = useState<HandheldTerminal | null>(null);
  const [assignUserSearch, setAssignUserSearch] = useState('');
  const [assignUserId, setAssignUserId] = useState(0);
  const [assignNotes, setAssignNotes] = useState('');
  const [assignEquipment, setAssignEquipment] = useState<string[]>([]);
  const [unassignTerminal, setUnassignTerminal] = useState<HandheldTerminal | null>(null);
  const [returnNotes, setReturnNotes] = useState('');
  const [returnEquipment, setReturnEquipment] = useState<string[]>([]);
  const [returnPhotos, setReturnPhotos] = useState<string[]>([]);
  const [zimmetBusy, setZimmetBusy] = useState(false);

  // Status filter state'i
  const [statusFilter, setStatusFilter] = useState<'all' | 'Aktif' | 'Pasif' | 'Arızalı'>('all');

  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    inventory_number: '',
    serial_number: '',
    ip_address: '',
    mac_address: '',
    assigned_user_id: 0,
    location_id: 0,
    department: '',
    status: 'Aktif',
    fault_details: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir el terminali açma
  useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'handheld-terminals' || !e.detail?.entityId) return;
      try {
        const terminal = await apiService.getHandheldTerminal(e.detail.entityId);
        if (terminal) handleViewHistory(terminal);
      } catch (err) { console.error('[openEntityById handheldterminals]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  // IP ve MAC adresi validasyonu ve duplicate kontrolü
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.ip_address) {
        validateAndCheckDuplicate('ip', formData.ip_address);
      }
      if (formData.mac_address) {
        validateAndCheckDuplicate('mac', formData.mac_address);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData.ip_address, formData.mac_address, editingTerminal?.id]);

  const validateIpAddress = (ip: string): boolean => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const validateMacAddress = (mac: string): boolean => {
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    return macRegex.test(mac);
  };

  const validateAndCheckDuplicate = async (type: 'ip' | 'mac', value: string) => {
    if (!value) {
      if (type === 'ip') setIpDuplicateWarning('');
      else setMacDuplicateWarning('');
      return;
    }

    // Format validasyonu
    if (type === 'ip' && !validateIpAddress(value)) {
      setIpDuplicateWarning(t('handheld.invalidIpFormat'));
      return;
    }
    if (type === 'mac' && !validateMacAddress(value)) {
      setMacDuplicateWarning(t('handheld.invalidMacFormat'));
      return;
    }

    // Duplicate kontrolü
    try {
      const response = await apiService.checkHandheldTerminalDuplicate(type, value, editingTerminal?.id);
      if (response.has_duplicate) {
        if (type === 'ip') {
          setIpDuplicateWarning(response.message || t('handheld.ipAlreadyUsed'));
        } else {
          setMacDuplicateWarning(response.message || t('handheld.macAlreadyUsed'));
        }
      } else {
        if (type === 'ip') setIpDuplicateWarning('');
        else setMacDuplicateWarning('');
      }
    } catch (error) {
      console.error('Duplicate check error:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [terminalsResponse, locationsResponse, usersResponse] = await Promise.all([
        apiService.getHandheldTerminals(),
        apiService.getLocations(),
        ['admin', 'superadmin'].includes(user?.role || '') ? apiService.getUsers() : Promise.resolve([])
      ]);
      
      setTerminalsData(Array.isArray(terminalsResponse) ? terminalsResponse : []);
      
      if (locationsResponse && locationsResponse.data) {
        setLocationsData(Array.isArray(locationsResponse.data) ? locationsResponse.data : []);
      } else if (Array.isArray(locationsResponse)) {
        setLocationsData(locationsResponse);
      } else {
        setLocationsData([]);
      }
      
      if (Array.isArray(usersResponse)) {
        setUsersData(usersResponse.filter(u => ['admin', 'superadmin', 'teknisyen', 'calisan'].includes(u.role)));
      } else {
        setUsersData([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('handheld.notifLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredTerminals = terminalsData.filter(terminal => {
    const matchesSearch =
      String(terminal.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(terminal.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(terminal.inventory_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(terminal.serial_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      terminal.ip_address?.includes(searchTerm) ||
      terminal.mac_address?.includes(searchTerm) ||
      String(terminal.assigned_user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(terminal.location_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(terminal.department || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    let matchesStatus = true;
    if (statusFilter === 'Aktif') {
      matchesStatus = terminal.status === 'Aktif' || !terminal.status;
    } else if (statusFilter === 'Pasif') {
      matchesStatus = terminal.status === 'Pasif';
    } else if (statusFilter === 'Arızalı') {
      matchesStatus = terminal.status === 'Arızalı';
    }
    // statusFilter === 'all' için hepsini göster

    return matchesSearch && matchesStatus;
  });

  // Sıralama
  const sortedTerminals = [...filteredTerminals].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: any = a[sortField as keyof HandheldTerminal];
    let bValue: any = b[sortField as keyof HandheldTerminal];

    if (typeof aValue === 'string') {
      aValue = String(aValue || '').toLowerCase();
      bValue = (bValue || '').toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedTerminals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTerminals = sortedTerminals.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortField, sortDirection]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 inline" />
      : <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };

  const handleAdd = () => {
    setEditingTerminal(null);
    setFormData({
      brand: '',
      model: '',
      inventory_number: '',
      serial_number: '',
      ip_address: '',
      mac_address: '',
      assigned_user_id: 0,
      location_id: 0,
      department: ''
    });
    setIpDuplicateWarning('');
    setMacDuplicateWarning('');
    setShowModal(true);
  };

  const handleEdit = (terminal: HandheldTerminal) => {
    setEditingTerminal(terminal);
    setFormData({
      brand: terminal.brand,
      model: terminal.model,
      inventory_number: terminal.inventory_number,
      serial_number: terminal.serial_number,
      ip_address: terminal.ip_address,
      mac_address: terminal.mac_address,
      assigned_user_id: terminal.assigned_user_id || 0,
      location_id: terminal.location_id,
      department: terminal.department || '',
      status: terminal.status || 'Aktif',
      fault_details: terminal.fault_details || ''
    });
    setIpDuplicateWarning('');
    setMacDuplicateWarning('');
    setShowModal(true);
  };

  const handleSave = async () => {
    // Validasyon
    if (!formData.brand || !formData.model) {
      showGlobalNotification.error(t('common.error'), t('handheld.notifBrandModelRequired'));
      return;
    }

    if (formData.ip_address && !validateIpAddress(formData.ip_address)) {
      showGlobalNotification.error(t('common.error'), t('handheld.notifInvalidIp'));
      return;
    }

    if (formData.mac_address && !validateMacAddress(formData.mac_address)) {
      showGlobalNotification.error(t('common.error'), t('handheld.notifInvalidMac'));
      return;
    }

    if (ipDuplicateWarning || macDuplicateWarning) {
      showGlobalNotification.error(t('common.error'), t('handheld.notifFixConflicts'));
      return;
    }

    if (!formData.location_id) {
      showGlobalNotification.error(t('common.error'), t('handheld.notifSelectLocation'));
      return;
    }

    if (formData.status === 'Arızalı' && !formData.fault_details?.trim()) {
      showGlobalNotification.error(t('common.error'), t('handheld.notifFaultRequired'));
      return;
    }

    try {
      if (editingTerminal) {
        await apiService.updateHandheldTerminal(editingTerminal.id, formData);
        showGlobalNotification.success(t('common.success'), t('handheld.notifUpdated'));
      } else {
        await apiService.createHandheldTerminal(formData);
        showGlobalNotification.success(t('common.success'), t('handheld.notifCreated'));
      }

      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving handheld terminal:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('handheld.notifSaveError'));
    }
  };

  const handleDelete = async (id: number) => {
    const terminal = terminalsData.find(t => t.id === id);
    if (!terminal) return;
    
    if (confirm(`"${terminal.brand} ${terminal.model}" (${terminal.inventory_number}) ${t('handheld.confirmDeleteSuffix')}`)) {
      try {
        await apiService.deleteHandheldTerminal(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('handheld.notifDeleted'));
      } catch (error: any) {
        console.error('Error deleting handheld terminal:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('handheld.notifDeleteError'));
      }
    }
  };

  const handleQuickCopy = (terminal: HandheldTerminal) => {
    setEditingTerminal(null);
    setFormData({
      brand: terminal.brand,
      model: terminal.model,
      inventory_number: '', // Kopyalanmayacak
      serial_number: '', // Kopyalanmayacak
      ip_address: '', // Kopyalanmayacak
      mac_address: '', // Kopyalanmayacak
      assigned_user_id: terminal.assigned_user_id || 0,
      location_id: terminal.location_id,
      department: terminal.department || '',
      status: 'Aktif',
      fault_details: ''
    });
    setIpDuplicateWarning('');
    setMacDuplicateWarning('');
    setShowModal(true);
    showGlobalNotification.info(t('common.info'), t('handheld.notifCopied'));
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredTerminals.map((terminal, index) => ({
        [t('handheld.excelColOrder')]: index + 1,
        [t('handheld.excelColBrand')]: terminal.brand,
        [t('handheld.excelColModel')]: terminal.model,
        [t('handheld.excelColInventoryNo')]: terminal.inventory_number,
        [t('handheld.excelColSerialNo')]: terminal.serial_number,
        [t('handheld.excelColIpAddress')]: terminal.ip_address || '',
        [t('handheld.excelColMacAddress')]: terminal.mac_address || '',
        [t('handheld.excelColAssignedUser')]: terminal.assigned_user_name || '',
        [t('handheld.excelColLocation')]: terminal.location_name || '',
        [t('handheld.excelColStatus')]: terminal.status || 'Aktif',
        [t('handheld.excelColFaultDetails')]: terminal.fault_details || '',
        [t('handheld.excelColDepartment')]: terminal.department || '',
        [t('handheld.excelColCreatedAt')]: new Date(terminal.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('handheld.excelSheet'));
      
      const colWidths = [
        { wch: 8 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 12 }, { wch: 30 }, { wch: 15 }, { wch: 15 }
      ];
      ws['!cols'] = colWidths;

      const filename = `el-terminalleri-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      showGlobalNotification.success(t('common.success'), `${filteredTerminals.length} ${t('handheld.notifExcelSuccess')}`);
    } catch (error: any) {
      console.error('Excel export error:', error);
      showGlobalNotification.error(t('common.error'), t('handheld.notifExcelError'));
    }
  };

  const handleTerminalSelection = (terminalId: number) => {
    setSelectedTerminals(prev => 
      prev.includes(terminalId) 
        ? prev.filter(id => id !== terminalId)
        : [...prev, terminalId]
    );
  };

  const selectAllFiltered = () => {
    const allIds = filteredTerminals.map(t => t.id);
    setSelectedTerminals(allIds);
  };

  const handleViewHistory = async (terminal: HandheldTerminal) => {
    setSelectedTerminalForHistory(terminal);
    setHistoryLoading(true);
    setShowHistoryModal(true);

    try {
      const history = await apiService.getHandheldTerminalHistory(terminal.id);
      setSelectedTerminalHistory(history);
    } catch (error: any) {
      console.error('Error loading terminal history:', error);
      showGlobalNotification.error(t('common.error'), t('handheld.notifHistoryError'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const deselectAll = () => {
    setSelectedTerminals([]);
  };

  const generateLabelPreview = () => {
    setShowLabelPreview(true);
  };

  const printLabels = async () => {
    const selectedTerminalData = terminalsData.filter(t => selectedTerminals.includes(t.id));
    const token = localStorage.getItem('auth_token');

    // Get QR codes for all selected terminals first
    const qrCodes: {[key: number]: string} = {};
    for (const terminal of selectedTerminalData) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/handheld-terminals/${terminal.id}/qr-code?token=${token}&embed=true`);
        const html = await response.text();

        // Extract base64 image from the HTML
        const imgMatch = html.match(/src="data:image\/png;base64,([^"]+)"/);
        if (imgMatch) {
          qrCodes[terminal.id] = imgMatch[0]; // src="data:image..."
        }
      } catch (error) {
        console.error('Error fetching QR code for terminal', terminal.id, error);
      }
    }

    // Create print-friendly page
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(generateLabelHTML(selectedTerminalData, qrCodes, labelSize));
    printWindow.document.close();

    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 1000);
  };

  // Etiket boyut konfigürasyonları
  const labelSizeConfigs: {[key: string]: {
    name: string;
    labelsPerPage: number;
    cols: number;
    rows: number;
    orientation: 'portrait' | 'landscape';
    labelWidth: string;
    labelHeight: string;
    qrSize: string;
    fontSize: {title: string; info: string};
  }} = {
    'landscape-6': {
      name: t('handheld.labelSizeLandscape6'),
      labelsPerPage: 6,
      cols: 3,
      rows: 2,
      orientation: 'landscape',
      labelWidth: '90mm',
      labelHeight: '85mm',
      qrSize: '35mm',
      fontSize: {title: '18px', info: '11px'}
    },
    'portrait-24': {
      name: t('handheld.labelSizePortrait24'),
      labelsPerPage: 24,
      cols: 3,
      rows: 8,
      orientation: 'portrait',
      labelWidth: '70mm',
      labelHeight: '37mm',
      qrSize: '25mm',
      fontSize: {title: '12px', info: '8px'}
    },
    'portrait-16': {
      name: t('handheld.labelSizePortrait16'),
      labelsPerPage: 16,
      cols: 2,
      rows: 8,
      orientation: 'portrait',
      labelWidth: '99mm',
      labelHeight: '34mm',
      qrSize: '28mm',
      fontSize: {title: '13px', info: '9px'}
    },
    'portrait-12': {
      name: t('handheld.labelSizePortrait12'),
      labelsPerPage: 12,
      cols: 2,
      rows: 6,
      orientation: 'portrait',
      labelWidth: '105mm',
      labelHeight: '48mm',
      qrSize: '35mm',
      fontSize: {title: '14px', info: '10px'}
    },
    'portrait-4': {
      name: t('handheld.labelSizePortrait4'),
      labelsPerPage: 4,
      cols: 2,
      rows: 2,
      orientation: 'portrait',
      labelWidth: '105mm',
      labelHeight: '148mm',
      qrSize: '55mm',
      fontSize: {title: '20px', info: '13px'}
    }
  };

  const handlePrintSingleLabel = (terminal: HandheldTerminal) => {
    setSingleLabelTerminal(terminal);
    setShowSingleLabelModal(true);
  };

  const printSingleLabel = async () => {
    if (!singleLabelTerminal) return;

    const token = localStorage.getItem('auth_token');
    const qrCodes: {[key: number]: string} = {};

    try {
      // Get QR code for the terminal
      const response = await fetch(`${import.meta.env.VITE_API_URL}/handheld-terminals/${singleLabelTerminal.id}/qr-code?token=${token}&embed=true`);
      const html = await response.text();

      // Extract base64 image from the HTML
      const imgMatch = html.match(/src="data:image\/png;base64,([^"]+)"/);
      if (imgMatch) {
        qrCodes[singleLabelTerminal.id] = imgMatch[0]; // src="data:image..."
      }

      // Create print-friendly page
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      printWindow.document.write(generateLabelHTML([singleLabelTerminal], qrCodes, labelSize));
      printWindow.document.close();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
      }, 1000);

      // Close modal
      setShowSingleLabelModal(false);
      setSingleLabelTerminal(null);
    } catch (error) {
      console.error('Error printing label for terminal', singleLabelTerminal.id, error);
      showGlobalNotification.error(t('common.error'), t('handheld.notifLabelError'));
    }
  };

  const generateLabelHTML = (terminals: HandheldTerminal[], qrCodes: {[key: number]: string}, sizeKey: string) => {
    const config = labelSizeConfigs[sizeKey];
    const labelsPerPage = config.labelsPerPage;
    const pages = [];

    for (let i = 0; i < terminals.length; i += labelsPerPage) {
      const pageLabels = terminals.slice(i, i + labelsPerPage);
      pages.push(pageLabels);
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('handheld.labelHTMLTitle')}</title>
        <style>
          @page {
            size: A4 ${config.orientation};
            margin: 8mm;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .page {
            display: grid;
            grid-template-columns: repeat(${config.cols}, 1fr);
            grid-template-rows: repeat(${config.rows}, 1fr);
            gap: 4mm;
            ${config.orientation === 'landscape' ? 'height: 180mm; width: 277mm;' : 'height: 277mm; width: 190mm;'}
            page-break-after: always;
          }
          .page:last-child {
            page-break-after: avoid;
          }
          .label {
            border: 2px solid #333;
            border-radius: 4px;
            padding: 3mm;
            display: flex;
            flex-direction: row;
            align-items: center;
            background: white;
            position: relative;
            overflow: hidden;
            gap: 3mm;
            width: ${config.labelWidth};
            height: ${config.labelHeight};
            box-sizing: border-box;
          }
          .label-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-width: 0;
          }
          .label-title {
            font-size: ${config.fontSize.title};
            font-weight: bold;
            color: #333;
            margin-bottom: 2mm;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .label-info {
            font-size: ${config.fontSize.info};
            color: #666;
            line-height: 1.3;
          }
          .label-info div {
            margin-bottom: 0.5mm;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .label .qr-code {
            width: ${config.qrSize};
            height: ${config.qrSize};
            flex-shrink: 0;
            border-radius: 3px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .label .qr-code img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        </style>
      </head>
      <body>
        ${pages.map(pageLabels => `
          <div class="page">
            ${pageLabels.map(terminal => `
              <div class="label">
                <div class="label-content">
                  <div class="label-title">${terminal.brand} ${terminal.model}</div>
                  <div class="label-info">
                    ${terminal.inventory_number ? `<div><strong>Env:</strong> ${terminal.inventory_number}</div>` : ''}
                    ${terminal.serial_number ? `<div><strong>Seri:</strong> ${terminal.serial_number}</div>` : ''}
                    ${terminal.location_name ? `<div><strong>Lok:</strong> ${terminal.location_name}</div>` : ''}
                    ${terminal.department ? `<div><strong>Böl:</strong> ${terminal.department}</div>` : ''}
                  </div>
                </div>
                <div class="qr-code">
                  ${qrCodes[terminal.id] ?
                    `<img ${qrCodes[terminal.id]} alt="QR Code" />` :
                    '<div style="background: #f0f0f0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 8px;">QR Yok</div>'
                  }
                </div>
              </div>
            `).join('')}
            ${Array(labelsPerPage - pageLabels.length).fill('').map(() => '<div class="label" style="border: none; visibility: hidden;"></div>').join('')}
          </div>
        `).join('')}
      </body>
      </html>
    `;
  };

  const exportToPDF = () => {
    try {
      const pdf = new jsPDF('l', 'mm', 'a4');
      
      pdf.setFontSize(16);
      pdf.text(t('handheld.pdfTitle'), 14, 15);

      pdf.setFontSize(10);
      pdf.text(`${t('handheld.pdfReportDate')} ${new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}`, 14, 22);
      pdf.text(`${t('handheld.pdfTotalTerminals')} ${filteredTerminals.length}`, 200, 22);
      
      const tableData = filteredTerminals.map((terminal, index) => [
        (index + 1).toString(),
        terminal.brand,
        terminal.model,
        terminal.inventory_number,
        terminal.serial_number,
        terminal.ip_address || '',
        terminal.mac_address || '',
        terminal.assigned_user_name || '',
        terminal.location_name || '',
        terminal.department || ''
      ]);

      (pdf as any).autoTable({
        head: [[t('handheld.pdfColOrder'), t('handheld.pdfColBrand'), t('handheld.pdfColModel'), t('handheld.pdfColInventoryNo'), t('handheld.pdfColSerialNo'), t('handheld.pdfColIpAddress'), t('handheld.pdfColMacAddress'), t('handheld.pdfColAssignedUser'), t('handheld.pdfColLocation'), t('handheld.pdfColDepartment')]],
        body: tableData,
        startY: 28,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
          0: { cellWidth: 12 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 30 },
          7: { cellWidth: 30 },
          8: { cellWidth: 30 },
          9: { cellWidth: 25 }
        },
        margin: { top: 28, left: 14, right: 14 }
      });

      const filename = `el-terminalleri-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      showGlobalNotification.success(t('common.success'), `${filteredTerminals.length} ${t('handheld.notifPdfSuccess')}`);
    } catch (error: any) {
      console.error('PDF export error:', error);
      showGlobalNotification.error(t('common.error'), t('handheld.notifPdfError'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ---- Zimmet (cep telefonlarındaki prosedürün aynısı) ----
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
  });

  const formatMysqlNow = () => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  };

  const handleOpenAssign = (terminal: HandheldTerminal) => {
    setAssignTerminal(terminal);
    setAssignUserSearch('');
    setAssignUserId(0);
    setAssignNotes('');
    setAssignEquipment([]);
  };

  const closeAssign = () => {
    setAssignTerminal(null);
    setAssignUserId(0);
    setAssignNotes('');
    setAssignEquipment([]);
  };

  const createAssignmentForm = async (body: Record<string, unknown>) => {
    const response = await fetch('/api/assignment-forms', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || 'Zimmet formu oluşturulamadı');
    }
    return response.json();
  };

  const confirmAssign = async (withForm: boolean) => {
    if (!assignTerminal || !assignUserId) return;
    setZimmetBusy(true);
    try {
      const response = await fetch(`/api/handheld-terminals/${assignTerminal.id}/quick-assign`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ assigned_user_id: assignUserId })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Atama başarısız');
      }

      if (withForm) {
        try {
          await createAssignmentForm({
            handheld_terminal_id: assignTerminal.id,
            assigned_user_id: assignUserId,
            notes: assignNotes,
            delivered_equipment: assignEquipment.length > 0 ? assignEquipment : undefined,
          });
        } catch (formError: any) {
          showGlobalNotification.error(t('common.error'), `Cihaz atandı ancak zimmet formu oluşturulamadı: ${formError.message}`);
          closeAssign();
          loadData();
          return;
        }
        showGlobalNotification.success(t('common.success'), 'Zimmet formu oluşturuldu ve el terminali atandı');
        window.location.href = '/assignment-forms';
        return;
      }

      showGlobalNotification.success(t('common.success'), `${assignTerminal.brand} ${assignTerminal.model} atandı`);
      closeAssign();
      loadData();
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message);
    } finally {
      setZimmetBusy(false);
    }
  };

  const handleOpenUnassign = async (terminal: HandheldTerminal) => {
    if (!terminal.assigned_user_id) return;
    setUnassignTerminal(terminal);
    setReturnNotes('');
    setReturnPhotos([]);
    setReturnEquipment([]);
    try {
      const resp = await fetch(`/api/assignment-forms/handheld/${terminal.id}/latest-equipment`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setReturnEquipment(data.delivered_equipment || []);
      }
    } catch {}
  };

  const closeUnassign = () => {
    setUnassignTerminal(null);
    setReturnNotes('');
    setReturnPhotos([]);
    setReturnEquipment([]);
  };

  const addReturnPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const toProcess = Math.min(files.length, 4 - returnPhotos.length);
    for (let i = 0; i < toProcess; i++) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxW = 1200, maxH = 1600;
          let { width, height } = img;
          if (width > maxW || height > maxH) {
            const ratio = Math.min(maxW / width, maxH / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          setReturnPhotos(prev => prev.length < 4 ? [...prev, dataUrl] : prev);
        };
        img.src = ev.target?.result as string;
      };
      reader.readAsDataURL(files[i]);
    }
    e.target.value = '';
  };

  const uploadReturnPhotos = async (formId: number, photos: string[]) => {
    if (photos.length === 0) return;
    const fd = new FormData();
    photos.forEach((dataUrl, i) => {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)![1];
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let n = 0; n < bstr.length; n++) u8arr[n] = bstr.charCodeAt(n);
      fd.append('photos', new Blob([u8arr], { type: mime }), `photo-${i + 1}.jpg`);
    });
    await fetch(`/api/assignment-forms/${formId}/return-photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      body: fd
    });
  };

  const confirmUnassign = async (withForm: boolean) => {
    if (!unassignTerminal) return;
    setZimmetBusy(true);
    try {
      let formNumber: string | null = null;

      // Önce tutanak (atanan kullanıcı bilgisi atama kalkmadan alınmalı)
      if (withForm) {
        const form = await createAssignmentForm({
          handheld_terminal_id: unassignTerminal.id,
          assigned_user_id: unassignTerminal.assigned_user_id,
          assigned_by_user_id: user?.id,
          assignment_date: formatMysqlNow(),
          form_type: 'return',
          notes: returnNotes || 'Zimmet geri teslim işlemi',
          received_equipment: returnEquipment.length > 0 ? returnEquipment : undefined,
        });
        formNumber = form.form_number;
        await uploadReturnPhotos(form.id, returnPhotos);
      }

      const response = await fetch(`/api/handheld-terminals/${unassignTerminal.id}/unassign`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ notes: returnNotes || 'Zimmet kaldırma işlemi' })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Zimmet kaldırılamadı');
      }

      showGlobalNotification.success(
        t('common.success'),
        formNumber
          ? `${unassignTerminal.brand} ${unassignTerminal.model} zimmeti kaldırıldı (Form No: ${formNumber})`
          : `${unassignTerminal.brand} ${unassignTerminal.model} zimmeti kaldırıldı`
      );
      closeUnassign();
      if (withForm) {
        window.location.href = '/assignment-forms';
      } else {
        loadData();
      }
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message);
    } finally {
      setZimmetBusy(false);
    }
  };

  // Satır işlem butonları: masaüstü tablo ve mobil kart görünümünde ortak
  const renderTerminalActions = (terminal: HandheldTerminal, variant: 'table' | 'card') => {
    const btn = variant === 'card'
      ? 'min-h-[44px] min-w-[44px] rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700'
      : 'p-1.5 min-h-[32px] min-w-[32px] rounded flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700';
    const icon = variant === 'card' ? 'w-5 h-5' : 'w-4 h-4';
    const canAssign = ['admin', 'superadmin'].includes(user?.role || '');
    return (
      <>
        {canAssign && (terminal.assigned_user_id ? (
          <button onClick={() => handleOpenUnassign(terminal)} className={`${btn} text-amber-600 hover:text-amber-800`} title="Zimmet Kaldır" aria-label="Zimmet Kaldır">
            <UserMinus className={icon} />
          </button>
        ) : (
          <button onClick={() => handleOpenAssign(terminal)} className={`${btn} text-emerald-600 hover:text-emerald-800`} title="Zimmetle" aria-label="Zimmetle">
            <UserPlus className={icon} />
          </button>
        ))}
        <button onClick={() => handleEdit(terminal)} className={`${btn} text-primary hover:text-primary-dark`} title={t('handheld.tooltipEdit')} aria-label={t('handheld.tooltipEdit')}>
          <Edit2 className={icon} />
        </button>
        <button onClick={() => handleQuickCopy(terminal)} className={`${btn} text-orange-600 hover:text-orange-900`} title={t('handheld.tooltipCopy')} aria-label={t('handheld.tooltipCopy')}>
          <Copy className={icon} />
        </button>
        <button onClick={() => handlePrintSingleLabel(terminal)} className={`${btn} text-purple-600 hover:text-purple-900`} title={t('handheld.tooltipPrintLabel')} aria-label={t('handheld.tooltipPrintLabel')}>
          <Printer className={icon} />
        </button>
        <button onClick={() => handleViewHistory(terminal)} className={`${btn} text-green-600 hover:text-green-900`} title={t('handheld.tooltipHistory')} aria-label={t('handheld.tooltipHistory')}>
          <Eye className={icon} />
        </button>
        <button onClick={() => handleDelete(terminal.id)} className={`${btn} ${variant === 'card' ? 'ml-auto' : 'ml-2'} text-red-600 hover:text-red-900`} title={t('handheld.tooltipDelete')} aria-label={t('handheld.tooltipDelete')}>
          <Trash2 className={icon} />
        </button>
      </>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('handheld.pageTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('handheld.pageSubtitle')}</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 text-sm min-h-[44px] justify-center"
            title={t('handheld.excelSheet')}
          >
            <Download className="w-4 h-4" />
            <span>Excel</span>
          </button>
          <button
            onClick={exportToPDF}
            className="bg-red-600 text-white px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 text-sm min-h-[44px] justify-center"
            title={t('handheld.pdfTitle')}
          >
            <FileText className="w-4 h-4" />
            <span>PDF</span>
          </button>
          <button
            onClick={handleAdd}
            className="bg-primary text-white px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg flex items-center gap-2 hover:bg-primary-dark text-sm min-h-[44px] justify-center"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('handheld.btnNewTerminal')}</span>
            <span className="sm:hidden">{t('handheld.btnNew')}</span>
          </button>
          <button
            onClick={() => setShowLabelModal(true)}
            className="bg-purple-600 text-white px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700 text-sm min-h-[44px] justify-center"
          >
            <QrCode className="w-4 h-4" />
            <span className="hidden sm:inline">{t('handheld.btnBulkLabel')}</span>
            <span className="sm:hidden">{t('handheld.btnLabel')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <button
          onClick={() => setStatusFilter('all')}
          className={`text-left rounded-lg shadow-sm border p-3 sm:p-6 transition-all ${
            statusFilter === 'all'
              ? 'bg-primary/10 border-primary/50 shadow-md'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-primary/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('handheld.statTotal')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{terminalsData.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary/20 rounded-lg hidden sm:flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('Aktif')}
          className={`text-left rounded-lg shadow-sm border p-3 sm:p-6 transition-all ${
            statusFilter === 'Aktif'
              ? 'bg-green-50 border-green-300 shadow-md'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-green-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('handheld.statActive')}</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">
                {terminalsData.filter(term => term.status === 'Aktif' || !term.status).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg hidden sm:flex items-center justify-center">
              <span className="text-2xl">✓</span>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('Pasif')}
          className={`text-left rounded-lg shadow-sm border p-3 sm:p-6 transition-all ${
            statusFilter === 'Pasif'
              ? 'bg-gray-100 dark:bg-gray-700 border-gray-400 shadow-md'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-300 dark:border-gray-600'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('handheld.statPassive')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-600 dark:text-gray-400">
                {terminalsData.filter(term => term.status === 'Pasif').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg hidden sm:flex items-center justify-center">
              <span className="text-2xl">⊘</span>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('Arızalı')}
          className={`text-left rounded-lg shadow-sm border p-3 sm:p-6 transition-all ${
            statusFilter === 'Arızalı'
              ? 'bg-red-50 border-red-300 shadow-md'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-red-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('handheld.statFaulty')}</p>
              <p className="text-xl sm:text-2xl font-bold text-red-600">
                {terminalsData.filter(term => term.status === 'Arızalı').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg hidden sm:flex items-center justify-center">
              <span className="text-2xl">!</span>
            </div>
          </div>
        </button>
      </div>

      {/* Aktif Filter Göstergesi */}
      {statusFilter !== 'all' && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('handheld.activeFilter')}</span>
          <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1">
            <span className="text-sm font-medium">
              {statusFilter === 'Aktif' && t('handheld.filterActive')}
              {statusFilter === 'Pasif' && t('handheld.filterPassive')}
              {statusFilter === 'Arızalı' && t('handheld.filterFaulty')}
            </span>
            <button
              onClick={() => setStatusFilter('all')}
              className="-my-3 -mr-3 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              aria-label={t('common.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">({filteredTerminals.length} {language === 'en' ? 'results' : 'sonuç'})</span>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder={t('handheld.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 border rounded-lg w-full max-w-md text-base"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('handheld.colOrder')}</th>
                <th
                  className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('brand')}
                >
                  {t('handheld.colBrand')} {getSortIcon('brand')}
                </th>
                <th
                  className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('model')}
                >
                  {t('handheld.colModel')} {getSortIcon('model')}
                </th>
                <th
                  className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('inventory_number')}
                >
                  {t('handheld.colInventoryNo')} {getSortIcon('inventory_number')}
                </th>
                <th
                  className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none hidden lg:table-cell"
                  onClick={() => handleSort('serial_number')}
                >
                  {t('handheld.colSerialNo')} {getSortIcon('serial_number')}
                </th>
                <th
                  className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none hidden xl:table-cell"
                  onClick={() => handleSort('ip_address')}
                >
                  {t('handheld.colIpAddress')} {getSortIcon('ip_address')}
                </th>
                <th
                  className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none hidden xl:table-cell"
                  onClick={() => handleSort('mac_address')}
                >
                  {t('handheld.colMacAddress')} {getSortIcon('mac_address')}
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden lg:table-cell">{t('handheld.colAssignedUser')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('handheld.colLocation')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden lg:table-cell">{t('handheld.colStatus')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden xl:table-cell">{t('handheld.colDepartment')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('handheld.colActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedTerminals.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center">
                    <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">{t('handheld.notFound')}</p>
                  </td>
                </tr>
              ) : (
                paginatedTerminals.map((terminal, index) => (
                  <tr key={terminal.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {startIndex + index + 1}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{terminal.brand}</td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{terminal.model}</td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{terminal.inventory_number || '-'}</td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden lg:table-cell">{terminal.serial_number || '-'}</td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden xl:table-cell">{terminal.ip_address || '-'}</td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden xl:table-cell">{terminal.mac_address || '-'}</td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden lg:table-cell">
                      {terminal.assigned_user_name ? (
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          {terminal.assigned_user_name}
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-4 lg:px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="truncate max-w-[150px]">{terminal.location_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">
                      {terminal.status === 'Aktif' && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{t('handheld.statusActive')}</span>
                      )}
                      {terminal.status === 'Pasif' && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">{t('handheld.statusPassive')}</span>
                      )}
                      {terminal.status === 'Arızalı' && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" title={terminal.fault_details}>{t('handheld.statusFaulty')}</span>
                      )}
                      {!terminal.status && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{t('handheld.statusActive')}</span>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden xl:table-cell">{terminal.department || '-'}</td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-1">
                        {renderTerminalActions(terminal, 'table')}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination for Desktop */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex sm:hidden items-center justify-between w-full">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('handheld.prevPage')}
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {language === 'en' ? `Page ${currentPage} / ${totalPages}` : `Sayfa ${currentPage} / ${totalPages}`}
                </span>
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {t('handheld.nextPage')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>

              <div className="hidden sm:flex items-center justify-between w-full">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {language === 'en' ? (
                    <>Showing <span className="font-medium">{startIndex + 1}-{Math.min(endIndex, sortedTerminals.length)}</span> of <span className="font-medium">{sortedTerminals.length}</span> handheld terminals</>
                  ) : (
                    <>Toplam <span className="font-medium">{sortedTerminals.length}</span> el terminalinden{' '}<span className="font-medium">{startIndex + 1}-{Math.min(endIndex, sortedTerminals.length)}</span> arası gösteriliyor</>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('handheld.prevPage')}
                  </button>

                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md min-h-[44px] ${
                            currentPage === pageNum
                              ? 'bg-primary text-white'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    {t('handheld.nextPage')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        <div className="flex items-center gap-2">
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            aria-label={language === 'en' ? 'Sort by' : 'Sırala'}
            className="flex-1 px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-base"
          >
            <option value="">{language === 'en' ? 'Sort: default' : 'Sıralama: varsayılan'}</option>
            <option value="brand">{t('handheld.colBrand')}</option>
            <option value="model">{t('handheld.colModel')}</option>
            <option value="inventory_number">{t('handheld.colInventoryNo')}</option>
            <option value="assigned_user_name">{t('handheld.cardUser').replace(/:$/, '')}</option>
            <option value="location_name">{t('handheld.cardLocation').replace(/:$/, '')}</option>
            <option value="status">{t('handheld.cardStatus').replace(/:$/, '')}</option>
          </select>
          <button
            type="button"
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            disabled={!sortField}
            aria-label={sortDirection === 'asc' ? (language === 'en' ? 'Ascending' : 'Artan') : (language === 'en' ? 'Descending' : 'Azalan')}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50"
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
        {paginatedTerminals.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
            <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">{t('handheld.notFound')}</p>
          </div>
        ) : (
          paginatedTerminals.map((terminal, index) => (
            <div key={terminal.id} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 break-words">{terminal.brand} {terminal.model}</h3>
                  {terminal.inventory_number && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('handheld.cardEnv')} {terminal.inventory_number}</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                {terminal.serial_number && (
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <span className="font-medium w-24 flex-shrink-0">{t('handheld.cardSerialNo')}</span>
                    <span className="text-gray-900 dark:text-gray-100 min-w-0 break-all">{terminal.serial_number}</span>
                  </div>
                )}
                {terminal.ip_address && (
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <span className="font-medium w-24 flex-shrink-0">IP:</span>
                    <span className="text-gray-900 dark:text-gray-100 min-w-0 break-all">{terminal.ip_address}</span>
                  </div>
                )}
                {terminal.mac_address && (
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <span className="font-medium w-24 flex-shrink-0">MAC:</span>
                    <span className="text-gray-900 dark:text-gray-100 min-w-0 break-all">{terminal.mac_address}</span>
                  </div>
                )}
                {terminal.assigned_user_name && (
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="font-medium w-20 flex-shrink-0">{t('handheld.cardUser')}</span>
                    <span className="text-gray-900 dark:text-gray-100 min-w-0 break-all">{terminal.assigned_user_name}</span>
                  </div>
                )}
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="font-medium w-20 flex-shrink-0">{t('handheld.cardLocation')}</span>
                  <span className="text-gray-900 dark:text-gray-100 min-w-0 break-all">{terminal.location_name || '-'}</span>
                </div>
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <span className="font-medium w-24 flex-shrink-0">{t('handheld.cardStatus')}</span>
                  <div>
                    {terminal.status === 'Aktif' && (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{t('handheld.statusActive')}</span>
                    )}
                    {terminal.status === 'Pasif' && (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">{t('handheld.statusPassive')}</span>
                    )}
                    {terminal.status === 'Arızalı' && (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" title={terminal.fault_details}>{t('handheld.statusFaulty')}</span>
                    )}
                    {!terminal.status && (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{t('handheld.statusActive')}</span>
                    )}
                  </div>
                </div>
                {terminal.department && (
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <span className="font-medium w-24 flex-shrink-0">{t('handheld.cardDepartment')}</span>
                    <span className="text-gray-900 dark:text-gray-100 min-w-0 break-all">{terminal.department}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1">
                {renderTerminalActions(terminal, 'card')}
              </div>
            </div>
          ))
        )}
        
        {/* Pagination for Mobile */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center justify-between w-full">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('handheld.prevPage')}
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'en' ? `Page ${currentPage} / ${totalPages}` : `Sayfa ${currentPage} / ${totalPages}`}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {t('handheld.nextPage')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {editingTerminal ? t('handheld.modalTitleEdit') : t('handheld.modalTitleAdd')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 -webkit-overflow-scrolling-touch">
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="px-4 pt-4 sm:px-6 sm:pt-6 space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formBrand')}
                    </label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formModel')}
                    </label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formInventoryNo')}
                    </label>
                    <input
                      type="text"
                      value={formData.inventory_number}
                      onChange={(e) => setFormData({...formData, inventory_number: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formSerialNo')}
                    </label>
                    <input
                      type="text"
                      value={formData.serial_number}
                      onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formIpAddress')}
                    </label>
                    <input
                      type="text"
                      value={formData.ip_address}
                      onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
                        ipDuplicateWarning ? 'border-red-300 bg-red-50' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="192.168.1.100"
                    />
                    {ipDuplicateWarning && (
                      <p className="text-xs text-red-600 mt-1">{ipDuplicateWarning}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formMacAddress')}
                    </label>
                    <input
                      type="text"
                      value={formData.mac_address}
                      onChange={(e) => setFormData({...formData, mac_address: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
                        macDuplicateWarning ? 'border-red-300 bg-red-50' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="00:1A:2B:3C:4D:5E"
                    />
                    {macDuplicateWarning && (
                      <p className="text-xs text-red-600 mt-1">{macDuplicateWarning}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formLocation')}
                    </label>
                    <select
                      value={formData.location_id}
                      onChange={(e) => setFormData({...formData, location_id: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                      required
                    >
                      <option value={0}>{t('handheld.formLocationPlaceholder')}</option>
                      {locationsData.map(location => (
                        <option key={location.id} value={location.id}>{location.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formAssignedUser')}
                    </label>
                    <select
                      value={formData.assigned_user_id}
                      onChange={(e) => setFormData({...formData, assigned_user_id: parseInt(e.target.value)})}
                      disabled={!!editingTerminal}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value={0}>{t('handheld.formUserPlaceholder')}</option>
                      {usersData.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                    {editingTerminal && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Zimmet işlemleri için listedeki "Zimmetle" / "Zimmet Kaldır" butonlarını kullanın.
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formDepartment')}
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                      placeholder={t('handheld.formDeptPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.formStatus')}
                    </label>
                    <select
                      value={formData.status || 'Aktif'}
                      onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                      required
                    >
                      <option value="Aktif">{t('handheld.formStatusActive')}</option>
                      <option value="Pasif">{t('handheld.formStatusPassive')}</option>
                      <option value="Arızalı">{t('handheld.formStatusFaulty')}</option>
                    </select>
                  </div>

                  <div>
                    {(formData.status === 'Arızalı') && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('handheld.formFaultDetails')} {formData.status === 'Arızalı' && <span className="text-red-600">*</span>}
                        </label>
                        <textarea
                          value={formData.fault_details || ''}
                          onChange={(e) => setFormData({...formData, fault_details: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
                          rows={4}
                          placeholder={t('handheld.formFaultPlaceholder')}
                          required={formData.status === 'Arızalı'}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {formData.status === 'Arızalı' && !formData.fault_details && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{t('handheld.faultRequiredWarning')}</p>
                  </div>
                )}

                <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 flex justify-end gap-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 sm:flex-none px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 transition-colors min-h-[44px] text-base"
                  >
                    {t('handheld.btnCancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 sm:flex-none px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 min-h-[44px] text-base"
                  >
                    <Save className="w-4 h-4" />
                    {editingTerminal ? t('handheld.btnUpdate') : t('handheld.btnCreate')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Modal>

      {/* Zimmetle Modal */}
      <Modal isOpen={!!assignTerminal} onClose={closeAssign}>
        {assignTerminal && (() => {
          const term = assignUserSearch.toLowerCase();
          const filteredUsers = usersData.filter(u =>
            String(u.name || '').toLowerCase().includes(term) || String(u.email || '').toLowerCase().includes(term)
          );
          return (
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">El Terminali Zimmetle</h2>
                <button onClick={closeAssign} className="text-gray-400 hover:text-gray-600 dark:text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>{assignTerminal.inventory_number}</strong> envanter numaralı
                  <strong> {assignTerminal.brand} {assignTerminal.model}</strong> el terminali için kullanıcı seçin.
                </p>
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={assignUserSearch}
                    onChange={(e) => setAssignUserSearch(e.target.value)}
                    placeholder="Kullanıcı ara..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                  {filteredUsers.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 dark:text-gray-400">Kullanıcı bulunamadı</div>
                  ) : filteredUsers.map(u => (
                    <div
                      key={u.id}
                      onClick={() => setAssignUserId(u.id)}
                      className={`p-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 ${assignUserId === u.id ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teslim Edilen Ekipmanlar</label>
                  <div className="grid grid-cols-2 gap-2">
                    {HANDHELD_EQUIPMENT_LIST.map(item => (
                      <label key={item} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={assignEquipment.includes(item)}
                          onChange={(e) => setAssignEquipment(prev => e.target.checked ? [...prev, item] : prev.filter(x => x !== item))}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notlar (Opsiyonel)</label>
                  <textarea
                    value={assignNotes}
                    onChange={(e) => setAssignNotes(e.target.value)}
                    placeholder="Özel durumlar vb."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 focus:ring-2 focus:ring-primary"
                    rows={2}
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => confirmAssign(true)}
                  disabled={!assignUserId || zimmetBusy}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Zimmet Formu ile Ata
                </button>
                <button
                  onClick={() => confirmAssign(false)}
                  disabled={!assignUserId || zimmetBusy}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Form Olmadan Ata
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Zimmet Kaldır / Geri Teslim Tutanağı Modal */}
      <Modal isOpen={!!unassignTerminal} onClose={closeUnassign}>
        {unassignTerminal && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Zimmet Geri Teslim Tutanağı</h2>
              <button onClick={closeUnassign} className="text-gray-400 hover:text-gray-600 dark:text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg text-sm">
                <div className="font-medium text-orange-800 dark:text-orange-300">
                  {unassignTerminal.brand} {unassignTerminal.model}
                </div>
                <div className="text-orange-600 dark:text-orange-400">Envanter: {unassignTerminal.inventory_number}</div>
                <div className="text-orange-600 dark:text-orange-400">Atanan: {unassignTerminal.assigned_user_name}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teslim Alınan Ekipmanlar</label>
                {returnEquipment.length > 0 && (
                  <div className="mb-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded px-3 py-2">
                    Zimmet sırasında teslim edilen ekipmanlar işaretlendi. Geri aldıklarınızı onaylayın veya güncelleyin.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {HANDHELD_EQUIPMENT_LIST.map(item => (
                    <label key={item} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={returnEquipment.includes(item)}
                        onChange={(e) => setReturnEquipment(prev => e.target.checked ? [...prev, item] : prev.filter(x => x !== item))}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Geri Teslim Notları (İsteğe Bağlı)</label>
                <textarea
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  placeholder="Cihazın durumu, geri teslim sebebi vb. notlar ekleyebilirsiniz..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 focus:ring-2 focus:ring-primary"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cihaz Fotoğrafları (En fazla 4 adet)</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {returnPhotos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img src={photo} alt={`Fotoğraf ${idx + 1}`} className="w-full h-full object-cover rounded-md border border-gray-300 dark:border-gray-600" />
                      <button
                        onClick={() => setReturnPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {returnPhotos.length < 4 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/10 transition-colors">
                      <Camera className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-400">Ekle</span>
                      <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addReturnPhoto} />
                    </label>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400">
                <div><strong>Teslim Eden:</strong> {unassignTerminal.assigned_user_name}</div>
                <div><strong>Teslim Alan:</strong> {user?.name}</div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                onClick={closeUnassign}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => confirmUnassign(false)}
                disabled={zimmetBusy}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Tutanak Olmadan Kaldır
              </button>
              <button
                onClick={() => confirmUnassign(true)}
                disabled={zimmetBusy}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                Tutanak ile Kaldır
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Terminal History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedTerminalForHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {selectedTerminalForHistory.brand} {selectedTerminalForHistory.model} - {t('handheld.historyTitle')}
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 -webkit-overflow-scrolling-touch">
              {historyLoading ? (
                <div className="p-6 text-center">
                  <p className="text-gray-600 dark:text-gray-400">{t('handheld.historyLoading')}</p>
                </div>
              ) : !selectedTerminalHistory || selectedTerminalHistory.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-600 dark:text-gray-400">{t('handheld.historyEmpty')}</p>
                </div>
              ) : (
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    {selectedTerminalHistory.map((entry, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                              {entry.action === 'create' && t('handheld.historyActionCreate')}
                              {entry.action === 'update' && t('handheld.historyActionUpdate')}
                              {entry.action === 'delete' && t('handheld.historyActionDelete')}
                              {entry.action === 'assign' && t('handheld.historyActionAssign')}
                              {entry.action === 'unassign' && t('handheld.historyActionUnassign')}
                              {!['create', 'update', 'delete', 'assign', 'unassign'].includes(entry.action) && entry.action}
                            </h4>
                            {entry.field_name && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('handheld.historyField')} {entry.field_name}</p>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(entry.changed_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          {entry.old_value && (
                            <div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">{t('handheld.historyOldValue')}</p>
                              <p className="text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 break-words">
                                {entry.old_value}
                              </p>
                            </div>
                          )}
                          {entry.new_value && (
                            <div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">{t('handheld.historyNewValue')}</p>
                              <p className="text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 break-words">
                                {entry.new_value}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                          <span>
                            {entry.changed_by_name ? `${t('handheld.historyChangedBy')} ${entry.changed_by_name}` : t('handheld.historySystem')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* Label Printing Modal */}
      <Modal isOpen={showLabelModal} onClose={() => setShowLabelModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <QrCode className="h-6 w-6" />
                {t('handheld.labelModalTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowLabelModal(false);
                  setSelectedTerminals([]);
                  setShowLabelPreview(false);
                }}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {!showLabelPreview ? (
                <>
                  {/* Label Size Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('handheld.labelSize')}
                    </label>
                    <select
                      value={labelSize}
                      onChange={(e) => setLabelSize(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                    >
                      {Object.entries(labelSizeConfigs).map(([key, config]) => (
                        <option key={key} value={key}>{config.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('handheld.labelSizeInfoPre')} {labelSizeConfigs[labelSize].labelsPerPage} {t('handheld.labelSizeInfoMid')} {labelSizeConfigs[labelSize].orientation === 'landscape' ? t('handheld.labelLandscape') : t('handheld.labelPortrait')} {t('handheld.labelSizeInfoSuffix')}
                    </p>
                  </div>

                  {/* Terminal Selection */}
                  <div className="mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('handheld.labelSelectionTitle')} ({selectedTerminals.length} {t('handheld.labelSelectionSelected')})
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllFiltered}
                          className="text-sm bg-primary text-white px-3 py-2 min-h-[40px] rounded hover:bg-primary-dark"
                        >
                          {t('handheld.labelSelectAll')}
                        </button>
                        <button
                          onClick={deselectAll}
                          className="text-sm bg-gray-600 text-white px-3 py-2 min-h-[40px] rounded hover:bg-gray-700"
                        >
                          {t('handheld.labelDeselectAll')}
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg">
                      <div className="grid grid-cols-1 gap-2 p-2 sm:p-4">
                        {filteredTerminals.map(terminal => (
                          <label
                            key={terminal.id}
                            className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTerminals.includes(terminal.id)}
                              onChange={() => handleTerminalSelection(terminal.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {terminal.brand} {terminal.model}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {terminal.inventory_number && `Env: ${terminal.inventory_number} - `}
                                {terminal.location_name && `${terminal.location_name}`}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-4 flex justify-end gap-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setShowLabelModal(false);
                        setSelectedTerminals([]);
                      }}
                      className="flex-1 sm:flex-none justify-center px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                    >
                      {t('handheld.btnCancel')}
                    </button>
                    <button
                      onClick={generateLabelPreview}
                      disabled={selectedTerminals.length === 0}
                      className="flex-1 sm:flex-none justify-center px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      {t('handheld.labelPreviewBtn')}
                    </button>
                  </div>
                </>
              ) : (
                /* Preview */
                <div>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t('handheld.labelPreviewTitle')} ({selectedTerminals.length} {t('handheld.labelPreviewItems')})
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {language === 'en'
                        ? `${Math.ceil(selectedTerminals.length / labelSizeConfigs[labelSize].labelsPerPage)} ${t('handheld.labelPreviewPages')}, ${labelSizeConfigs[labelSize].labelsPerPage} ${t('handheld.labelPreviewLabels')} ${t('handheld.labelPreviewPerPage')} (${labelSizeConfigs[labelSize].orientation === 'landscape' ? t('handheld.labelLandscape') : t('handheld.labelPortrait')})`
                        : `${Math.ceil(selectedTerminals.length / labelSizeConfigs[labelSize].labelsPerPage)} ${t('handheld.labelPreviewPages')}, ${t('handheld.labelPreviewPerPage')} ${labelSizeConfigs[labelSize].labelsPerPage} ${t('handheld.labelPreviewLabels')} (${labelSizeConfigs[labelSize].orientation === 'landscape' ? t('handheld.labelLandscape') : t('handheld.labelPortrait')})`
                      }
                    </div>
                  </div>

                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mb-4 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {terminalsData.filter(term => selectedTerminals.includes(term.id)).map((terminal) => (
                        <div key={terminal.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 flex items-center gap-3">
                          <div className="flex-1">
                            <h4 className="font-bold text-sm mb-1">{terminal.brand} {terminal.model}</h4>
                            <div className="text-xs space-y-1">
                              {terminal.inventory_number && <div>{t('handheld.cardEnv')} {terminal.inventory_number}</div>}
                              {terminal.serial_number && <div>{t('handheld.cardSerialNo')} {terminal.serial_number}</div>}
                              {terminal.location_name && <div>{t('handheld.cardLocation')} {terminal.location_name}</div>}
                            </div>
                          </div>
                          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center text-xs flex-shrink-0">
                            QR
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-4 flex justify-end gap-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShowLabelPreview(false)}
                      className="flex-1 sm:flex-none justify-center px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                    >
                      {t('handheld.labelBack')}
                    </button>
                    <button
                      onClick={printLabels}
                      className="flex-1 sm:flex-none justify-center px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      {t('handheld.labelPrint')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>

      {/* Single Label Printing Modal */}
      <Modal isOpen={showSingleLabelModal} onClose={() => setShowSingleLabelModal(false)}>
          {singleLabelTerminal && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Printer className="h-5 w-5" />
                {t('handheld.singleLabelTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowSingleLabelModal(false);
                  setSingleLabelTerminal(null);
                }}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {/* Terminal Info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {singleLabelTerminal.brand} {singleLabelTerminal.model}
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {singleLabelTerminal.inventory_number && (
                    <div>{t('handheld.singleLabelInventoryNo')} {singleLabelTerminal.inventory_number}</div>
                  )}
                  {singleLabelTerminal.serial_number && (
                    <div>{t('handheld.singleLabelSerialNo')} {singleLabelTerminal.serial_number}</div>
                  )}
                  {singleLabelTerminal.location_name && (
                    <div>{t('handheld.singleLabelLocation')} {singleLabelTerminal.location_name}</div>
                  )}
                </div>
              </div>

              {/* Label Size Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('handheld.labelSize')}
                </label>
                <select
                  value={labelSize}
                  onChange={(e) => setLabelSize(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                >
                  {Object.entries(labelSizeConfigs).map(([key, config]) => (
                    <option key={key} value={key}>{config.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {labelSizeConfigs[labelSize].labelsPerPage} {t('handheld.singleLabelPerPage')} {labelSizeConfigs[labelSize].orientation === 'landscape' ? t('handheld.labelLandscape') : t('handheld.labelPortrait')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowSingleLabelModal(false);
                    setSingleLabelTerminal(null);
                  }}
                  className="flex-1 sm:flex-none justify-center px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                >
                  {t('handheld.btnCancel')}
                </button>
                <button
                  onClick={printSingleLabel}
                  className="flex-1 sm:flex-none justify-center px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  {t('handheld.labelPrint')}
                </button>
              </div>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default HandheldTerminalsPage;

